'use client'

import { faviconUrl } from '@/components/ui/SourceMeta'
import { BIAS_WING } from '@/constants/bias'
import { useEffect, useRef, useState } from 'react'

// An outlet's round favicon with its lean as a ring (the concept,
// 2026-11-01): the ring's colour is the outlet's step on Pano's seven-step
// scale, so a column of headlines shows who wrote each one before the name
// is read. Used where outlets are listed by name (Read the coverage, Most
// active); the outlet's name always sits beside it, so the picture is
// decorative. No favicon uploaded: the outlet's initials in the same ring.

const RING = [
  [-18, 'ring-blue-700'],
  [-12, 'ring-blue-600'],
  [-BIAS_WING, 'ring-blue-400'],
  [BIAS_WING, 'ring-zinc-500'],
  [12, 'ring-red-400'],
  [18, 'ring-red-600'],
]

function ringOf(bias) {
  if (typeof bias !== 'number') return 'ring-zinc-500'
  for (const [below, ring] of RING) if (bias < below) return ring
  return 'ring-red-700'
}

const SIZES = {
  sm: 'h-4 w-4 text-[7px] ring-[1.5px] ring-offset-1',
  md: 'h-[22px] w-[22px] text-[8px] ring-2 ring-offset-2',
}

function initials(name = '') {
  const words = name
    .replace(/^The\s+/i, '')
    .split(/[\s-]+/)
    .filter((w) => w && !/^(of|and|the|news)$/i.test(w))
  return (
    words.length > 1 ? words.slice(0, 2).map((w) => w[0]) : [name.slice(0, 2)]
  )
    .join('')
    .toUpperCase()
}

export default function OutletIcon({ outlet, size = 'md', className = '' }) {
  const [failed, setFailed] = useState(false)
  const img = useRef(null)
  // A favicon that failed before hydration fired its error event before
  // React was listening: check once on mount.
  useEffect(() => {
    const el = img.current
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (el && el.complete && !el.naturalWidth) setFailed(true)
  }, [])
  if (!outlet?.slug) return null
  const frame = `${SIZES[size] || SIZES.md} ${ringOf(outlet.bias)} ring-offset-background inline-flex shrink-0 rounded-full ${className}`
  if (failed)
    return (
      <span
        aria-hidden="true"
        className={`${frame} items-center justify-center bg-muted font-sans font-semibold text-muted-foreground`}
      >
        {initials(outlet.name)}
      </span>
    )
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      ref={img}
      src={faviconUrl(outlet.slug)}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
      className={`${frame} bg-white object-cover`}
    />
  )
}
